
import {
  Vue, Component, Prop, Watch,
} from 'vue-property-decorator';
import { OperationInfo, PermissionCode, User } from '@/types';
import EditLeadSupplyDialog from '@/components/EditLeadSupplyDialog.vue';
import SupplyHistoryIcon from '@/components/SupplyHistoryIcon.vue';
import API from '@/lib/API';
import { formatEuro } from '@/helpers';

@Component({
  components: {
    EditLeadSupplyDialog,
    SupplyHistoryIcon,
  },
})
export default class OperationSupply extends Vue {
  @Prop(Object) readonly operationInfo!: OperationInfo;
  @Prop({ type: Number, default: 1 }) readonly childrenCount!: number;

  editDialog = false

  loading = false

  supplyNotes: string | null = null
  loadSupplyDetailsTimeout: number | undefined

  // Dati supply caricati dall'API
  supplyData: {
    supply1: boolean;
    supply1text: string;
    supply2: boolean;
    supply2text: string;
    supply3: boolean;
    supply3text: string;
    supply4: boolean;
    supply4text: string;
    supply5: boolean;
    supply5text: string;
  } | null = null

  @Watch('editDialog')
  onEditDialogChange(val: boolean) {
    // Se è stato chiuso il dialog...
    if (val === false) {
      // ... nasconde il tooltip
      this.$nextTick(() => {
        (this.$refs.tippyRef as any).tippy().hide();
      });

      // ... emette l'evento "close"
      this.$emit('close');

      // Non è necessario chiamare updateOperation manualmente:
      // il backend invierà un segnale WebSocket (SOCKET_LEAD_UPDATED)
      // che aggiornerà automaticamente la commessa nel pool
    }
  }

  @Watch('amIUpdating')
  onAmIUpdatingChange(val: boolean, oldVal: boolean) {
    // console.log(`OperationLead: onAmIUpdatingChange() val = ${val} oldVal ${oldVal}`);
    if (oldVal === true && val === false) {
      // La commessa è stata aggiornata
      // Se sono stati scaricati in precedenza dei dettagli, ricaricali
      if (this.supplyNotes || this.supplyData) {
        this.loadSupplyDetails();
      }
    }
  }

  get poolUpdating(): { [key: number]: boolean } {
    return this.$store.getters.currentOperationsPoolUpdating;
  }

  get amIUpdating(): boolean {
    return this.operationInfo
      && this.operationInfo.id in this.poolUpdating
      && this.poolUpdating[this.operationInfo.id] === true;
  }

  get hasSupplyNotes(): boolean {
    return this.operationInfo?.hasSupplyNotes === true;
  }

  get supplyText(): string | null {
    return this.operationInfo && 'supplyText' in this.operationInfo ? this.operationInfo.supplyText : null;
  }

  get supplyLabel(): string | null {
    return this.operationInfo && 'supplyLabel' in this.operationInfo ? this.operationInfo.supplyLabel : null;
  }

  get totalContractValue(): string | null {
    return this.operationInfo && 'totalContractValue' in this.operationInfo ? this.operationInfo.totalContractValue : null;
  }

  // Una nota di soli spazi o a capo non conta: accenderebbe pallino e segnaposto per niente
  get totalContractValueNotes(): string | null {
    const notes = this.operationInfo && 'totalContractValueNotes' in this.operationInfo
      ? this.operationInfo.totalContractValueNotes : null;
    return notes?.trim() || null;
  }

  get showTotalContractValue(): boolean {
    return this.hasPermission('totalContractValue.show')
      && (!!this.totalContractValue || !!this.totalContractValueNotes);
  }

  // Senza fornitura il popup va aperto lo stesso se c'e' il totale o la sua nota
  get hasTooltipContent(): boolean {
    return !!this.supplyText || this.showTotalContractValue;
  }

  get totalContractValueText(): string {
    return this.totalContractValue ? formatEuro(this.totalContractValue) : 'importo da definire';
  }

  formatEuro(value: string | null): string {
    return formatEuro(value);
  }

  get supply1(): boolean {
    return this.supplyData?.supply1 ?? false;
  }

  get supply2(): boolean {
    return this.supplyData?.supply2 ?? false;
  }

  get supply3(): boolean {
    return this.supplyData?.supply3 ?? false;
  }

  get supply4(): boolean {
    return this.supplyData?.supply4 ?? false;
  }

  get supply5(): boolean {
    return this.supplyData?.supply5 ?? false;
  }

  get supply1text(): string {
    return this.supplyData?.supply1text ?? '';
  }

  get supply2text(): string {
    return this.supplyData?.supply2text ?? '';
  }

  get supply3text(): string {
    return this.supplyData?.supply3text ?? '';
  }

  get supply4text(): string {
    return this.supplyData?.supply4text ?? '';
  }

  get supply5text(): string {
    return this.supplyData?.supply5text ?? '';
  }

  get isEditable() {
    if (!this.operationInfo) return false;

    // Se l'utente non ha accesso alla lead padre, non può modificare la fornitura
    if (!this.isCurrentUserAllowed) return false;

    return this.operationInfo
      && 'status' in this.operationInfo
      && (this.operationInfo.status === 'open' || this.operationInfo.status === 'new')
      && this.hasPermission('supply.edit');
  }

  async loadSupplyDetails() {
    try {
      const result = await API.getLeadSupply(this.operationInfo.id);
      if (!(result instanceof Error)) {
        // Carica i dati supply (storico forniture)
        this.supplyData = {
          supply1: !!result.supply1,
          supply1text: result.supply1text || '',
          supply2: !!result.supply2,
          supply2text: result.supply2text || '',
          supply3: !!result.supply3,
          supply3text: result.supply3text || '',
          supply4: !!result.supply4,
          supply4text: result.supply4text || '',
          supply5: !!result.supply5,
          supply5text: result.supply5text || '',
        };

        // Carica le note supply se presenti
        if (result.supplyNotes) {
          this.supplyNotes = result.supplyNotes.replace(/\n/g, '<br>');
        }
      }
    } catch (error) {
      console.log('OperationSupply: loadSupplyDetails error =', error); // eslint-disable-line no-console
    }
  }

  async onEdit() {
    this.loading = true;

    // Determina quale lead selezionare: padre o figlia elettrica
    let leadIdToSelect = this.operationInfo.id;

    // Se l'utente non ha accesso diretto al padre, ma ha accesso alla figlia elettrica
    if (!this.operationAllowedUsers.includes(this.user.id) && this.elettricaChildAllowedUsers.includes(this.user.id)) {
      // Trova l'ID della figlia elettrica
      const operation = this.operationInfo as any;
      if (operation.children && typeof operation.children === 'object') {
        const childrenArray = Object.values(operation.children);
        const elettricaChild = childrenArray.find((child: any) => child.type === 'elettrica-figlia');
        if (elettricaChild) {
          leadIdToSelect = (elettricaChild as any).id;
        }
      }
    }

    await this.$store.dispatch('selectLead', { id: leadIdToSelect });

    this.$nextTick(() => {
      (this.$refs.tippyRef as any).tippy().hide();
    });

    this.editDialog = true;

    this.loading = false;
  }

  get user(): User {
    return this.$store.getters.currentUser;
  }

  get userPermissions(): PermissionCode[] {
    return this.$store.getters.currentUserPermissions;
  }

  get operationAllowedUsers(): number[] {
    return this.operationInfo.allowedUsers.substring(1, this.operationInfo.allowedUsers.length - 1).split(':')
      .map((s: string) => Number(s));
  }

  get elettricaChildAllowedUsers(): number[] {
    if (!this.operationInfo || !('children' in this.operationInfo)) {
      return [];
    }

    const operation = this.operationInfo as any; // Cast temporaneo per accedere a children
    if (!operation.children || typeof operation.children !== 'object') {
      return [];
    }

    // Cerca la figlia elettrica nell'oggetto children
    const childrenArray = Object.values(operation.children);
    const elettricaChild = childrenArray.find((child: any) => child.type === 'elettrica-figlia');

    if (!elettricaChild || !(elettricaChild as any).allowedUsers) {
      return [];
    }

    return (elettricaChild as any).allowedUsers.substring(1, (elettricaChild as any).allowedUsers.length - 1).split(':')
      .map((s: string) => Number(s));
  }

  get isCurrentUserAllowed(): boolean {
    // Controlla accesso diretto al padre
    if (this.operationAllowedUsers.includes(this.user.id)) {
      return true;
    }

    // Se non ha accesso al padre, controlla se ha accesso alla figlia elettrica
    return this.elettricaChildAllowedUsers.includes(this.user.id);
  }

  hasPermission(codename: PermissionCode) {
    if (!this.user) return false;
    if (this.user.isSuperUser) return true;

    return this.userPermissions.includes(codename);
  }

  onTooltipShow() {
    console.log('OperationSupply: onTooltipShow()'); // eslint-disable-line no-console

    // Se i dati sono già stati caricati, non ricaricarli
    if (this.supplyData) return;

    // Carica i dati dopo un breve delay
    this.loadSupplyDetailsTimeout = setTimeout(async () => {
      console.log('OperationSupply: onTooltipShow() timeout!'); // eslint-disable-line no-console

      this.loadSupplyDetails();
    }, 1000);
  }

  onTooltipHide() {
    clearTimeout(this.loadSupplyDetailsTimeout);
  }
}
